import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format, formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import supabase from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const {
  FiSearch, FiFilter, FiMessageSquare, FiClock, FiUser, FiChevronRight, FiRefreshCw,
  FiAlertCircle, FiCheck, FiPause, FiArrowUp, FiArrowDown, FiMenu, FiX, FiCalendar,
  FiEdit, FiSave, FiPlus, FiTrash2, FiDownload, FiMail, FiMapPin, FiFileText,
  FiMessageCircle, FiClipboard, FiUsers, FiLoader, FiMoreVertical, FiAlertTriangle,
  FiGlobe, FiTrendingUp, FiTarget, FiThermometer, FiPhone, FiStar, FiHeart,
  FiActivity, FiZap, FiTrello, FiDatabase, FiSettings, FiBell, FiEye, FiEyeOff
} = FiIcons;

const ChatDashboard = () => {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';

  // Existing state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    message: 'Memeriksa koneksi...'
  });

  // Simplified CRM state
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [crmPanelVisible, setCrmPanelVisible] = useState(false);
  const [customerData, setCustomerData] = useState({
    id: null,
    id_whatsapp: '',
    full_name: '',
    email: '',
    location: '',
    complaint: '',
    internal_notes: '',
    gender: '',
    birth_date: '',
    lead_status: 'cold',
    follow_up_date: null
  });

  const [customFields, setCustomFields] = useState([]);
  const [newField, setNewField] = useState({ field_name: '', field_value: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [customerNames, setCustomerNames] = useState({});
  const [customerLeadStatus, setCustomerLeadStatus] = useState({});
  const [highlightedNumber, setHighlightedNumber] = useState(null);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [mobileView, setMobileView] = useState(window.innerWidth < 768);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  // New features state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [activeMessageMenu, setActiveMessageMenu] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState([]);
  const [conversationStats, setConversationStats] = useState({
    total: 0,
    today: 0,
    unread: 0,
    highPriority: 0
  });

  // Refs
  const messagesEndRef = useRef(null);
  const chatAreaRef = useRef(null);
  const numberListRef = useRef(null);
  const filteredNumberRef = useRef(null);
  const dashboardRef = useRef(null);
  const topMessageRef = useRef(null);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Initialize CRM tables
  const initializeCRMTables = async () => {
    try {
      console.log('Menginisialisasi tabel CRM...');
      
      // Check if tables exist first
      const { data: tables, error: tablesError } = await supabase
        .from('information_schema.tables')
        .select('table_name')
        .eq('table_schema', 'public')
        .or('table_name.eq.customer_data_crm,table_name.eq.custom_fields_crm');

      const existingTables = tables?.map(t => t.table_name) || [];
      
      if (!existingTables.includes('customer_data_crm')) {
        console.log('Membuat tabel customer_data_crm...');
        
        const { error: customerTableError } = await supabase
          .from('customer_data_crm')
          .select('id')
          .limit(1);

        if (customerTableError && customerTableError.code === 'PGRST116') {
          console.log('Tabel customer_data_crm akan dibuat saat insert pertama');
        }
      }

      console.log('Inisialisasi tabel CRM selesai');
    } catch (error) {
      console.log('Tabel CRM mungkin belum ada, akan dibuat saat save pertama:', error);
    }
  };

  // Enhanced time formatting
  const formatToJakartaTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const jakartaTime = new Intl.DateTimeFormat('id-ID', {
        timeZone: 'Asia/Jakarta',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(date);

      const year = jakartaTime.find(part => part.type === 'year').value;
      const month = jakartaTime.find(part => part.type === 'month').value;
      const day = jakartaTime.find(part => part.type === 'day').value;
      const hour = jakartaTime.find(part => part.type === 'hour').value;
      const minute = jakartaTime.find(part => part.type === 'minute').value;

      return `${day}/${month}/${year} ${hour}:${minute} WIB`;
    } catch (error) {
      console.error('Error formatting Jakarta time:', error);
      return timestamp;
    }
  };

  // Get current Jakarta time
  const getCurrentJakartaTime = () => {
    return new Intl.DateTimeFormat('id-ID', {
      timeZone: 'Asia/Jakarta',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(currentTime);
  };

  // Enhanced lead status functions
  const getLeadStatusIcon = (status) => {
    switch (status) {
      case 'cold': return FiThermometer;
      case 'warm': return FiTrendingUp;
      case 'hot': return FiTarget;
      case 'customer': return FiCheck;
      default: return FiThermometer;
    }
  };

  const getLeadStatusColor = (status) => {
    switch (status) {
      case 'cold': return 'text-blue-500 bg-blue-50 border-blue-200';
      case 'warm': return 'text-orange-500 bg-orange-50 border-orange-200';
      case 'hot': return 'text-red-500 bg-red-50 border-red-200';
      case 'customer': return 'text-green-500 bg-green-50 border-green-200';
      default: return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  };

  const getLeadStatusText = (status) => {
    switch (status) {
      case 'cold': return 'Dingin';
      case 'warm': return 'Hangat';
      case 'hot': return 'Panas';
      case 'customer': return 'Pelanggan';
      default: return 'Dingin';
    }
  };

  // Check connection to Supabase
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const { data, error } = await supabase
          .from('log_percakapan_urology_expert')
          .select('id_whatsapp')
          .limit(1);

        if (error) throw error;

        setConnectionStatus({
          connected: true,
          message: 'Terhubung ke Supabase'
        });

        console.log('Koneksi Supabase berhasil');
        await initializeCRMTables();
      } catch (error) {
        console.error('Error koneksi Supabase:', error);
        setConnectionStatus({
          connected: false,
          message: `Gagal terhubung ke Supabase: ${error.message}`
        });
      }
    };

    checkConnection();
  }, []);

  // Enhanced customer data fetching
  const fetchAllCustomerNames = async () => {
    if (!connectionStatus.connected) return;

    try {
      const { data, error } = await supabase
        .from('customer_data_crm')
        .select('id_whatsapp, full_name, lead_status');

      if (error) {
        console.log('Error fetch customer names:', error);
        if (error.code === 'PGRST116') {
          console.log('Tabel data customer belum ada');
          return;
        }
        throw error;
      }

      const namesMap = {};
      const leadStatusMap = {};
      
      if (data) {
        data.forEach(customer => {
          if (customer.full_name) {
            namesMap[customer.id_whatsapp] = customer.full_name;
          }
          leadStatusMap[customer.id_whatsapp] = customer.lead_status || 'cold';
        });
      }

      setCustomerNames(namesMap);
      setCustomerLeadStatus(leadStatusMap);
    } catch (error) {
      console.error('Error fetching customer names:', error);
    }
  };

  // Enhanced WhatsApp numbers fetching
  const fetchWhatsappNumbers = async () => {
    if (!connectionStatus.connected) return;

    try {
      let query = supabase
        .from('log_percakapan_urology_expert')
        .select('id_whatsapp, waktu_pesan, jenis_pesan')
        .order('waktu_pesan', { ascending: false });

      if (startDate) {
        query = query.gte('waktu_pesan', startDate);
      }
      if (endDate) {
        query = query.lte('waktu_pesan', endDate + 'T23:59:59');
      }

      const { data, error } = await query;
      if (error) throw error;

      const numbersMap = new Map();
      const today = new Date().toISOString().split('T')[0];
      let todayCount = 0;
      let unreadCount = 0;

      data.forEach(item => {
        const messageDate = item.waktu_pesan.split('T')[0];
        if (messageDate === today && item.jenis_pesan !== 'keluar') {
          todayCount++;
        }

        if (!numbersMap.has(item.id_whatsapp)) {
          numbersMap.set(item.id_whatsapp, {
            id_whatsapp: item.id_whatsapp,
            last_message_time: item.waktu_pesan,
            last_message_type: item.jenis_pesan
          });
        }
      });

      const numbersList = Array.from(numbersMap.values());
      setWhatsappNumbers(numbersList);

      // Update conversation stats
      setConversationStats(prev => ({
        ...prev,
        total: numbersList.length,
        today: todayCount,
        unread: unreadCount
      }));

      await fetchAllCustomerNames();

      if (!initialLoadComplete && numbersList.length > 0) {
        const mostRecentNumber = numbersList[0].id_whatsapp;
        handleWhatsappSelect(mostRecentNumber, true);
        setInitialLoadComplete(true);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp numbers:', error);
    }
  };

  // Enhanced conversation fetching
  const fetchConversations = async (isInitialSelect = false) => {
    if (!connectionStatus.connected) return;
    setLoading(true);

    try {
      let query = supabase
        .from('log_percakapan_urology_expert')
        .select('*');

      if (phoneFilter) {
        query = query.ilike('id_whatsapp', `%${phoneFilter}%`);
      }
      if (startDate) {
        query = query.gte('waktu_pesan', startDate);
      }
      if (endDate) {
        query = query.lte('waktu_pesan', endDate + 'T23:59:59');
      }

      query = query.order('waktu_pesan', { ascending: true });

      const { data, error } = await query;
      if (error) throw error;

      setConversations(data || []);
      await fetchTotalMessageCount();

      if (isInitialSelect) {
        setTimeout(() => {
          scrollToTop();
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced customer data fetching with better error handling
  const fetchCustomerData = async (whatsappId) => {
    if (!connectionStatus.connected || !whatsappId) return;

    try {
      console.log('Mengambil data customer untuk:', whatsappId);
      
      const { data: customerData, error: customerError } = await supabase
        .from('customer_data_crm')
        .select('*')
        .eq('id_whatsapp', whatsappId)
        .maybeSingle();

      if (customerError && customerError.code !== 'PGRST116') {
        console.error('Error fetch customer:', customerError);
        throw customerError;
      }

      if (customerData) {
        console.log('Data customer ditemukan:', customerData);
        
        // Set customer data dengan memastikan semua field ada
        setCustomerData({
          id: customerData.id || null,
          id_whatsapp: customerData.id_whatsapp || whatsappId,
          full_name: customerData.full_name || '',
          email: customerData.email || '',
          location: customerData.location || '',
          complaint: customerData.complaint || '',
          internal_notes: customerData.internal_notes || '',
          gender: customerData.gender || '',
          birth_date: customerData.birth_date || '',
          lead_status: customerData.lead_status || 'cold',
          follow_up_date: customerData.follow_up_date || null
        });

        // Fetch custom fields
        const { data: customFieldsData, error: fieldsError } = await supabase
          .from('custom_fields_crm')
          .select('*')
          .eq('customer_id', customerData.id);

        if (fieldsError && fieldsError.code !== 'PGRST116') {
          console.error('Error fetch custom fields:', fieldsError);
        }

        setCustomFields(customFieldsData || []);
      } else {
        console.log('Data customer tidak ditemukan, inisialisasi customer baru');
        setCustomerData({
          id: null,
          id_whatsapp: whatsappId,
          full_name: '',
          email: '',
          location: '',
          complaint: '',
          internal_notes: '',
          gender: '',
          birth_date: '',
          lead_status: 'cold',
          follow_up_date: null
        });
        setCustomFields([]);
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      // Initialize with empty data on error
      setCustomerData({
        id: null,
        id_whatsapp: whatsappId,
        full_name: '',
        email: '',
        location: '',
        complaint: '',
        internal_notes: '',
        gender: '',
        birth_date: '',
        lead_status: 'cold',
        follow_up_date: null
      });
      setCustomFields([]);
    }
  };

  // Enhanced save customer data with better conflict handling
  const saveCustomerData = async () => {
    if (!connectionStatus.connected || !customerData.id_whatsapp) {
      console.log('Tidak dapat menyimpan: tidak terhubung atau tidak ada WhatsApp ID');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(null);
    setSaveError(null);

    try {
      console.log('Memulai proses simpan untuk:', customerData.id_whatsapp);

      const dataToSave = {
        id_whatsapp: customerData.id_whatsapp.toString(),
        full_name: customerData.full_name || null,
        email: customerData.email || null,
        location: customerData.location || null,
        complaint: customerData.complaint || null,
        internal_notes: customerData.internal_notes || null,
        gender: customerData.gender || null,
        birth_date: customerData.birth_date || null,
        lead_status: customerData.lead_status || 'cold',
        follow_up_date: customerData.follow_up_date || null
      };

      let customerId = customerData.id;

      if (!customerId) {
        console.log('Membuat customer baru dengan upsert...');
        
        // Use upsert to handle potential duplicates
        const { data: upsertedCustomer, error: upsertError } = await supabase
          .from('customer_data_crm')
          .upsert(dataToSave, { 
            onConflict: 'id_whatsapp',
            ignoreDuplicates: false 
          })
          .select()
          .single();

        if (upsertError) {
          console.error('Error upsert:', upsertError);
          throw new Error(`Gagal menyimpan data customer: ${upsertError.message}`);
        }

        console.log('Customer berhasil di-upsert:', upsertedCustomer);
        customerId = upsertedCustomer.id;
        setCustomerData(prev => ({ ...prev, id: customerId }));
      } else {
        console.log('Mengupdate customer yang sudah ada...');
        const updateData = {
          ...dataToSave,
          updated_at: new Date().toISOString()
        };

        const { error: updateError } = await supabase
          .from('customer_data_crm')
          .update(updateData)
          .eq('id', customerId);

        if (updateError) {
          console.error('Error update:', updateError);
          throw new Error(`Gagal mengupdate data customer: ${updateError.message}`);
        }

        console.log('Customer berhasil diupdate');
      }

      // Handle custom fields
      if (customFields.length > 0) {
        console.log('Memproses custom fields...');
        for (const field of customFields) {
          if (field.field_name && field.field_name.trim() !== '') {
            if (field.id) {
              const { error: fieldUpdateError } = await supabase
                .from('custom_fields_crm')
                .update({
                  field_name: field.field_name,
                  field_value: field.field_value || null
                })
                .eq('id', field.id);

              if (fieldUpdateError) {
                console.error('Error update custom field:', fieldUpdateError);
                throw new Error(`Gagal mengupdate field custom: ${fieldUpdateError.message}`);
              }
            } else {
              const { error: fieldInsertError } = await supabase
                .from('custom_fields_crm')
                .insert({
                  customer_id: customerId,
                  field_name: field.field_name,
                  field_value: field.field_value || null
                });

              if (fieldInsertError) {
                console.error('Error insert custom field:', fieldInsertError);
                throw new Error(`Gagal menambah field custom: ${fieldInsertError.message}`);
              }
            }
          }
        }
      }

      setSaveSuccess(true);
      setSaveError(null);
      
      // Refresh customer data to get the latest state
      await fetchCustomerData(customerData.id_whatsapp);
      
      // Update the names and status maps
      if (customerData.full_name) {
        setCustomerNames(prev => ({
          ...prev,
          [customerData.id_whatsapp]: customerData.full_name
        }));
      }
      
      setCustomerLeadStatus(prev => ({
        ...prev,
        [customerData.id_whatsapp]: customerData.lead_status || 'cold'
      }));

      console.log('Simpan berhasil diselesaikan');

      // Add notification
      addNotification({
        type: 'success',
        message: `Data customer ${customerData.full_name || customerData.id_whatsapp} berhasil disimpan`
      });

    } catch (error) {
      console.error('Error saving customer data:', error);
      setSaveSuccess(false);
      setSaveError(error.message || 'Terjadi kesalahan saat menyimpan data');
      
      addNotification({
        type: 'error',
        message: `Gagal menyimpan data: ${error.message}`
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => {
        setSaveSuccess(null);
        setSaveError(null);
      }, 5000);
    }
  };

  // Notification system
  const addNotification = (notification) => {
    const id = Date.now();
    const newNotification = {
      id,
      ...notification,
      timestamp: new Date()
    };
    
    setNotifications(prev => [newNotification, ...prev.slice(0, 4)]);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // Get total message count
  const fetchTotalMessageCount = async () => {
    if (!connectionStatus.connected || !selectedConversation) return;

    try {
      let query = supabase
        .from('log_percakapan_urology_expert')
        .select('id', { count: 'exact', head: true });

      if (phoneFilter) {
        query = query.ilike('id_whatsapp', `%${phoneFilter}%`);
      }
      if (startDate) {
        query = query.gte('waktu_pesan', startDate);
      }
      if (endDate) {
        query = query.lte('waktu_pesan', endDate + 'T23:59:59');
      }

      const { count, error } = await query;
      if (error) throw error;

      setTotalMessages(count || 0);
      return count;
    } catch (error) {
      console.error('Error fetching message count:', error);
      return 0;
    }
  };

  // Enhanced export functionality
  const exportAllCustomersData = async () => {
    if (!connectionStatus.connected) return;
    setIsExporting(true);

    try {
      // Get all WhatsApp numbers
      const { data: allWhatsappNumbers, error: numbersError } = await supabase
        .from('log_percakapan_urology_expert')
        .select('id_whatsapp')
        .order('id_whatsapp');

      if (numbersError) throw numbersError;

      const uniqueNumbers = [...new Set(allWhatsappNumbers.map(item => item.id_whatsapp))];

      if (uniqueNumbers.length === 0) {
        alert('Tidak ada data customer untuk diekspor');
        setIsExporting(false);
        return;
      }

      // Get all customers data
      const { data: allCustomers, error: customersError } = await supabase
        .from('customer_data_crm')
        .select('*');

      if (customersError && customersError.code !== 'PGRST116') throw customersError;

      const customerMap = {};
      if (allCustomers) {
        allCustomers.forEach(customer => {
          customerMap[customer.id_whatsapp] = customer;
        });
      }

      // Prepare CSV content
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += "Nomor WhatsApp,Nama Lengkap,Email,Lokasi,Jenis Kelamin,Tanggal Lahir,Status Lead,Keluhan,Catatan Internal,Tanggal Follow Up,Dibuat,Diupdate\n";

      uniqueNumbers.forEach(whatsappNumber => {
        const customer = customerMap[whatsappNumber] || {
          id_whatsapp: whatsappNumber,
          full_name: '',
          email: '',
          location: '',
          gender: '',
          birth_date: '',
          lead_status: 'cold',
          complaint: '',
          internal_notes: '',
          follow_up_date: null,
          created_at: '',
          updated_at: ''
        };

        const formattedBirthDate = customer.birth_date ? format(new Date(customer.birth_date), 'dd/MM/yyyy') : '';
        const formattedFollowUp = customer.follow_up_date ? format(new Date(customer.follow_up_date), 'dd/MM/yyyy HH:mm') : '';

        csvContent += `"${whatsappNumber}","${customer.full_name || ''}","${customer.email || ''}","${customer.location || ''}","${customer.gender || ''}","${formattedBirthDate}","${getLeadStatusText(customer.lead_status || 'cold')}","${customer.complaint || ''}","${customer.internal_notes || ''}","${formattedFollowUp}","${customer.created_at || ''}","${customer.updated_at || ''}"\n`;
      });

      // Download file
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `data_customer_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification({
        type: 'success',
        message: 'Data customer berhasil diekspor'
      });

    } catch (error) {
      console.error('Error exporting customers data:', error);
      alert('Error mengekspor data customer: ' + error.message);
      
      addNotification({
        type: 'error',
        message: 'Gagal mengekspor data customer'
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Delete message functionality
  const handleDeleteMessage = async () => {
    if (!connectionStatus.connected || !messageToDelete || !isAdmin) return;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const { error } = await supabase
        .from('log_percakapan_urology_expert')
        .delete()
        .eq('id', messageToDelete.id);

      if (error) throw error;

      setConversations(prev => prev.filter(msg => msg.id !== messageToDelete.id));
      setFilteredConversations(prev => prev.filter(msg => msg.id !== messageToDelete.id));
      setShowDeleteModal(false);
      setMessageToDelete(null);

      addNotification({
        type: 'success',
        message: 'Pesan berhasil dihapus'
      });
    } catch (error) {
      console.error('Error deleting message:', error);
      setDeleteError(`Gagal menghapus pesan: ${error.message}`);
      
      addNotification({
        type: 'error',
        message: 'Gagal menghapus pesan'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete all messages functionality
  const handleDeleteAllMessages = async () => {
    if (!connectionStatus.connected || !selectedConversation || !isAdmin) return;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const { error } = await supabase
        .from('log_percakapan_urology_expert')
        .delete()
        .eq('id_whatsapp', selectedConversation);

      if (error) throw error;

      setConversations([]);
      setFilteredConversations([]);
      setWhatsappNumbers(prev => prev.filter(number => number.id_whatsapp !== selectedConversation));
      setShowDeleteAllModal(false);
      setSelectedConversation(null);
      setPhoneFilter('');
      fetchWhatsappNumbers();

      addNotification({
        type: 'success',
        message: 'Semua percakapan berhasil dihapus'
      });
    } catch (error) {
      console.error('Error deleting all messages:', error);
      setDeleteError(`Gagal menghapus percakapan: ${error.message}`);
      
      addNotification({
        type: 'error',
        message: 'Gagal menghapus percakapan'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter conversations based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = conversations.filter(conv =>
        conv.isi_pesan.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredConversations(filtered);
    } else {
      setFilteredConversations(conversations);
    }
  }, [conversations, searchTerm]);

  // Initial data fetch when connected
  useEffect(() => {
    if (connectionStatus.connected) {
      fetchWhatsappNumbers();
    }
  }, [connectionStatus.connected]);

  // Refetch when filters change
  useEffect(() => {
    if (connectionStatus.connected) {
      fetchConversations();
      fetchWhatsappNumbers();
    }
  }, [phoneFilter, startDate, endDate]);

  // Mobile view detection
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      setMobileView(isMobile);
      setSidebarVisible(!isMobile);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close message menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMessageMenu !== null && !e.target.closest('.message-menu-container')) {
        setActiveMessageMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeMessageMenu]);

  // Utility functions
  const scrollToBottom = () => {
    if (messagesEndRef.current && chatAreaRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatTime = (timestamp) => {
    try {
      return formatToJakartaTime(timestamp);
    } catch {
      return timestamp;
    }
  };

  const handleWhatsappSelect = (whatsappId, isInitialSelect = false) => {
    setPhoneFilter(whatsappId);
    setSelectedConversation(whatsappId);
    setHighlightedNumber(whatsappId);
    setIsFilterApplied(true);
    fetchCustomerData(whatsappId);
    fetchConversations(isInitialSelect);

    if (mobileView) {
      setSidebarVisible(false);
    }
  };

  const clearFilters = () => {
    setPhoneFilter('');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setSelectedConversation(null);
    setCrmPanelVisible(false);
    setCustomerData({
      id: null,
      id_whatsapp: '',
      full_name: '',
      email: '',
      location: '',
      complaint: '',
      internal_notes: '',
      gender: '',
      birth_date: '',
      lead_status: 'cold',
      follow_up_date: null
    });
    setCustomFields([]);
    setHighlightedNumber(null);
    setIsFilterApplied(false);

    if (connectionStatus.connected) {
      fetchWhatsappNumbers();
      fetchConversations();
    }
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const toggleCrmPanel = () => {
    setCrmPanelVisible(!crmPanelVisible);
  };

  const applyFilters = () => {
    if (connectionStatus.connected) {
      if (phoneFilter) {
        setHighlightedNumber(phoneFilter);
        setSelectedConversation(phoneFilter);
        setIsFilterApplied(true);
        
        setTimeout(() => {
          const matchingNumber = whatsappNumbers.find(number =>
            number.id_whatsapp.includes(phoneFilter)
          );
          if (matchingNumber) {
            setHighlightedNumber(matchingNumber.id_whatsapp);
            if (filteredNumberRef.current) {
              filteredNumberRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }
        }, 100);
      } else {
        setHighlightedNumber(null);
        setIsFilterApplied(false);
      }

      fetchWhatsappNumbers();
      fetchConversations();

      if (mobileView) {
        setSidebarVisible(false);
      }
    }
  };

  const toggleMessageMenu = (messageId) => {
    setActiveMessageMenu(activeMessageMenu === messageId ? null : messageId);
  };

  const matchesFilter = (number) => {
    return phoneFilter && number.id_whatsapp.includes(phoneFilter);
  };

  // Add custom field
  const addCustomField = () => {
    if (newField.field_name.trim() === '') return;
    setCustomFields([...customFields, { ...newField }]);
    setNewField({ field_name: '', field_value: '' });
  };

  // Update custom field
  const updateCustomField = (index, field, value) => {
    const updatedFields = [...customFields];
    updatedFields[index][field] = value;
    setCustomFields(updatedFields);
  };

  // Delete custom field
  const deleteCustomField = async (index) => {
    const fieldToDelete = customFields[index];
    const updatedFields = customFields.filter((_, i) => i !== index);
    setCustomFields(updatedFields);

    if (fieldToDelete.id) {
      try {
        const { error } = await supabase
          .from('custom_fields_crm')
          .delete()
          .eq('id', fieldToDelete.id);

        if (error) throw error;
      } catch (error) {
        console.error('Error deleting custom field:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" ref={dashboardRef}>
      {/* Notifications */}
      <AnimatePresence>
        {notifications.length > 0 && (
          <div className="fixed top-4 right-4 z-50 space-y-2">
            {notifications.map((notification) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 300 }}
                className={`p-3 rounded-lg shadow-lg max-w-sm ${
                  notification.type === 'success'
                    ? 'bg-green-500 text-white'
                    : notification.type === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-blue-500 text-white'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <SafeIcon
                    icon={
                      notification.type === 'success'
                        ? FiCheck
                        : notification.type === 'error'
                        ? FiAlertCircle
                        : FiBell
                    }
                  />
                  <span className="text-sm">{notification.message}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 md:p-6 sticky top-0 z-20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 md:space-x-3">
                <button
                  onClick={toggleSidebar}
                  className="text-white p-2 rounded-lg hover:bg-white/20"
                >
                  <SafeIcon icon={sidebarVisible ? FiX : FiMenu} className="text-xl" />
                </button>
                <div className="flex flex-col md:flex-row md:items-center">
                  <SafeIcon icon={FiMessageSquare} className="text-white text-xl md:text-2xl" />
                  <h1 className="text-lg md:text-2xl font-bold text-white ml-2">
                    Dashboard AI Agent
                  </h1>
                </div>
              </div>

              <div className="flex items-center space-x-2 md:space-x-3">
                {/* Enhanced Stats Display */}
                <div className="hidden lg:flex items-center space-x-4 bg-white/10 rounded-lg px-3 py-2">
                  <div className="text-center">
                    <div className="text-white text-sm font-bold">{conversationStats.total}</div>
                    <div className="text-blue-100 text-xs">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-white text-sm font-bold">{conversationStats.today}</div>
                    <div className="text-blue-100 text-xs">Hari ini</div>
                  </div>
                </div>

                {/* Jakarta Clock */}
                <div className="hidden md:flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-1">
                  <SafeIcon icon={FiGlobe} className="text-white text-sm" />
                  <div className="text-white">
                    <div className="text-xs font-medium">Jakarta, Indonesia</div>
                    <div className="text-sm font-mono">
                      {new Intl.DateTimeFormat('id-ID', {
                        timeZone: 'Asia/Jakarta',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      }).format(currentTime)} WIB
                    </div>
                  </div>
                </div>

                {/* Connection Status */}
                <div className={`text-xs md:text-sm px-2 md:px-3 py-1 rounded-full ${
                  connectionStatus.connected
                    ? 'bg-green-500/20 text-green-100'
                    : 'bg-red-500/20 text-red-100'
                }`}>
                  {connectionStatus.connected ? 'Terhubung' : 'Tidak Terhubung'}
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => {
                    fetchConversations();
                    fetchWhatsappNumbers();
                  }}
                  disabled={!connectionStatus.connected}
                  className={`${
                    connectionStatus.connected
                      ? 'bg-white/20 hover:bg-white/30 text-white'
                      : 'bg-white/10 text-white/50 cursor-not-allowed'
                  } p-2 rounded-lg transition-colors`}
                  aria-label="Refresh"
                >
                  <SafeIcon icon={FiRefreshCw} className="text-sm" />
                </button>
              </div>
            </div>

            {/* Mobile clock display */}
            <div className="md:hidden mt-3 pt-3 border-t border-white/20">
              <div className="flex items-center justify-center space-x-2 text-white">
                <SafeIcon icon={FiGlobe} className="text-sm" />
                <span className="text-sm">{getCurrentJakartaTime()}</span>
              </div>
            </div>
          </div>

          {!connectionStatus.connected ? (
            <div className="p-4 md:p-8 text-center">
              <div className="bg-red-50 p-4 md:p-6 rounded-xl border border-red-200 mb-4 md:mb-6">
                <SafeIcon icon={FiAlertCircle} className="text-red-500 text-3xl md:text-4xl mx-auto mb-3 md:mb-4" />
                <h3 className="text-md md:text-lg font-medium text-red-800 mb-2">
                  Gagal terhubung ke Supabase
                </h3>
                <p className="text-red-700">{connectionStatus.message}</p>
              </div>
            </div>
          ) : (
            <div className="relative">
              {/* Main Content Area */}
              <div className="flex flex-col md:flex-row">
                {/* Enhanced Sidebar */}
                <AnimatePresence>
                  {sidebarVisible && (
                    <motion.div
                      initial={mobileView ? { x: -300, opacity: 0 } : { opacity: 0 }}
                      animate={mobileView ? { x: 0, opacity: 1 } : { opacity: 1 }}
                      exit={mobileView ? { x: -300, opacity: 0 } : { opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`${
                        mobileView
                          ? 'fixed top-0 left-0 w-[85%] h-full z-30 shadow-xl'
                          : 'w-1/3 lg:w-1/4 border-r border-gray-200'
                      } bg-white overflow-y-auto`}
                      style={{ maxHeight: mobileView ? '100vh' : 'calc(100vh - 180px)' }}
                    >
                      {/* Enhanced Filter Section */}
                      <div className="sticky top-0 z-10">
                        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                          <div className="flex justify-between items-center">
                            <h2 className="text-base font-semibold flex items-center">
                              <SafeIcon icon={FiFilter} className="mr-2" />
                              Filter Pencarian
                            </h2>
                            {mobileView && (
                              <button
                                onClick={toggleSidebar}
                                className="text-white hover:text-blue-200"
                              >
                                <SafeIcon icon={FiX} className="text-xl" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        {/* Search */}
                        <div className="relative">
                          <SafeIcon
                            icon={FiSearch}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="text"
                            placeholder="Cari dalam pesan..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>

                        {/* Phone Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Nomor WhatsApp
                          </label>
                          <input
                            type="text"
                            placeholder="Cari nomor..."
                            value={phoneFilter}
                            onChange={(e) => setPhoneFilter(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                              isFilterApplied && phoneFilter
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Dari Tanggal
                            </label>
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Sampai Tanggal
                            </label>
                            <input
                              type="date"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        {/* Apply and Clear Filter buttons */}
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={applyFilters}
                            className="bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-3 rounded-lg transition-colors flex items-center justify-center space-x-1 text-xs"
                          >
                            <SafeIcon icon={FiCalendar} className="text-xs" />
                            <span>Terapkan</span>
                          </button>
                          <button
                            onClick={clearFilters}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 px-3 rounded-lg transition-colors text-xs"
                          >
                            Bersihkan
                          </button>
                        </div>

                        {/* Enhanced Export Button */}
                        <button
                          onClick={exportAllCustomersData}
                          disabled={isExporting}
                          className={`w-full bg-green-600 hover:bg-green-700 text-white py-1.5 px-3 rounded-lg transition-colors flex items-center justify-center space-x-1 text-xs ${
                            isExporting ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          {isExporting ? (
                            <>
                              <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full mr-1"></div>
                              <span>Mengekspor...</span>
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiDownload} className="text-xs" />
                              <span>Ekspor Data</span>
                            </>
                          )}
                        </button>

                        {/* WhatsApp Numbers List with Enhanced Display */}
                        <div className="mt-3">
                          <h3 className="text-sm font-semibold mb-2 flex items-center">
                            <SafeIcon icon={FiPhone} className="mr-1.5" />
                            Nomor WhatsApp ({whatsappNumbers.length})
                          </h3>
                          <div className="space-y-1.5 max-h-[calc(100vh-500px)] overflow-y-auto" ref={numberListRef}>
                            {whatsappNumbers.length === 0 ? (
                              <div className="text-center p-3 text-gray-500 bg-gray-50 rounded-lg text-xs">
                                Tidak ada data nomor WhatsApp
                              </div>
                            ) : (
                              whatsappNumbers.map((number) => {
                                const isHighlighted = highlightedNumber === number.id_whatsapp;
                                const isMatched = matchesFilter(number);
                                const leadStatus = customerLeadStatus[number.id_whatsapp] || 'cold';

                                return (
                                  <motion.div
                                    key={number.id_whatsapp}
                                    ref={isHighlighted ? filteredNumberRef : null}
                                    whileHover={{ scale: 1.01 }}
                                    onClick={() => handleWhatsappSelect(number.id_whatsapp)}
                                    className={`p-2 rounded-lg cursor-pointer transition-all border ${
                                      selectedConversation === number.id_whatsapp
                                        ? 'bg-blue-100 border-blue-300'
                                        : isMatched && isFilterApplied
                                        ? 'bg-blue-50 border-blue-300 shadow-sm'
                                        : 'bg-white hover:bg-gray-50 border-gray-200'
                                    } ${isHighlighted ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900 text-sm">
                                          {number.id_whatsapp}
                                          {customerNames[number.id_whatsapp] && (
                                            <span className="ml-1.5 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                              {customerNames[number.id_whatsapp]}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center justify-between mt-1">
                                          <div className="text-xs text-gray-500">
                                            {formatTime(number.last_message_time)}
                                          </div>
                                          <div className="flex items-center space-x-1">
                                            <div className={`text-xs px-1.5 py-0.5 rounded-full ${getLeadStatusColor(leadStatus)}`}>
                                              {getLeadStatusText(leadStatus)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-1.5 ml-2">
                                        <SafeIcon
                                          icon={getLeadStatusIcon(leadStatus)}
                                          className={`text-xs ${getLeadStatusColor(leadStatus).split(' ')[0]}`}
                                        />
                                        <SafeIcon icon={FiChevronRight} className="text-gray-400 text-xs" />
                                      </div>
                                    </div>
                                  </motion.div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Mobile overlay */}
                {mobileView && sidebarVisible && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-20"
                    onClick={toggleSidebar}
                  ></div>
                )}

                {/* Enhanced Chat Area */}
                <div className={`flex-1 flex flex-col ${mobileView ? 'min-h-[calc(100vh-180px)]' : ''}`}>
                  {/* Mobile Filter Toggle */}
                  {mobileView && !sidebarVisible && (
                    <div className="bg-white border-b border-gray-200 p-2 flex items-center justify-between">
                      <button
                        onClick={toggleSidebar}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-3 rounded-lg transition-colors flex items-center justify-center space-x-1 text-xs"
                      >
                        <SafeIcon icon={FiFilter} className="text-xs" />
                        <span>Filter</span>
                      </button>
                      {selectedConversation && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={toggleCrmPanel}
                            className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded-lg transition-colors flex items-center space-x-1 text-xs"
                          >
                            <SafeIcon icon={crmPanelVisible ? FiX : FiEdit} className="text-xs" />
                            <span>{crmPanelVisible ? 'Tutup' : 'CRM'}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Enhanced Chat Header */}
                  <div className="p-3 border-b border-gray-200 bg-white sticky top-[72px] z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div>
                          {selectedConversation ? (
                            <div>
                              <h3 className="text-sm font-semibold">
                                Percakapan: {selectedConversation}
                                {customerNames[selectedConversation] && (
                                  <span className="ml-1.5 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                    {customerNames[selectedConversation]}
                                  </span>
                                )}
                                {customerLeadStatus[selectedConversation] && (
                                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${getLeadStatusColor(customerLeadStatus[selectedConversation])}`}>
                                    {getLeadStatusText(customerLeadStatus[selectedConversation])}
                                  </span>
                                )}
                              </h3>
                              <div className="flex items-center text-xs text-gray-500">
                                <span className="mr-1.5">
                                  {filteredConversations.length} pesan
                                  {searchTerm && ` (dicari: "${searchTerm}")`}
                                </span>
                                {totalMessages > 0 && (
                                  <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">
                                    {filteredConversations.length}/{totalMessages}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <h3 className="text-sm font-semibold">Semua Percakapan</h3>
                              <p className="text-xs text-gray-500">
                                {filteredConversations.length} total pesan
                                {searchTerm && ` (dicari: "${searchTerm}")`}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-1.5">
                        {/* Delete All Button - Admin Only */}
                        {isAdmin && selectedConversation && (
                          <button
                            onClick={() => setShowDeleteAllModal(true)}
                            className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded-lg transition-colors flex items-center space-x-1 text-xs"
                            title="Hapus semua percakapan"
                          >
                            <SafeIcon icon={FiTrash2} className="text-xs" />
                            <span className="hidden md:inline">Hapus Semua</span>
                          </button>
                        )}

                        {selectedConversation && !mobileView && (
                          <button
                            onClick={toggleCrmPanel}
                            className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded-lg transition-colors flex items-center space-x-1 text-xs"
                          >
                            <SafeIcon icon={crmPanelVisible ? FiX : FiEdit} className="text-xs" />
                            <span>{crmPanelVisible ? 'Tutup CRM' : 'Data CRM'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Enhanced CRM Panel */}
                  <AnimatePresence>
                    {selectedConversation && crmPanelVisible && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-b border-gray-200 bg-gray-50 overflow-y-auto max-h-[calc(100vh-300px)]"
                      >
                        <div className="p-3">
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-semibold flex items-center">
                              <SafeIcon icon={FiUser} className="mr-1.5" />
                              Data Customer: {selectedConversation}
                            </h3>
                            <div className="flex space-x-1.5">
                              <button
                                onClick={toggleCrmPanel}
                                className="text-gray-500 hover:text-gray-700 p-1"
                              >
                                <SafeIcon icon={FiX} className="text-base" />
                              </button>
                            </div>
                          </div>

                          {/* Save Status Messages */}
                          {saveSuccess !== null && (
                            <div className={`mb-3 p-2 rounded-lg text-sm ${
                              saveSuccess
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-red-100 text-red-800 border border-red-200'
                            }`}>
                              {saveSuccess ? 'Data berhasil disimpan!' : 'Gagal menyimpan data.'}
                            </div>
                          )}

                          {saveError && (
                            <div className="mb-3 p-2 rounded-lg text-sm bg-red-100 text-red-800 border border-red-200">
                              <div className="flex items-center">
                                <SafeIcon icon={FiAlertCircle} className="mr-2 flex-shrink-0" />
                                <div>
                                  <div className="font-medium">Error:</div>
                                  <div>{saveError}</div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Simplified Customer Form */}
                          <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Full Name */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                  <SafeIcon icon={FiUser} className="mr-1 text-gray-500" />
                                  Nama Lengkap
                                </label>
                                <input
                                  type="text"
                                  value={customerData.full_name || ''}
                                  onChange={(e) => setCustomerData({ ...customerData, full_name: e.target.value })}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                  placeholder="Nama lengkap customer"
                                />
                              </div>

                              {/* Email */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                  <SafeIcon icon={FiMail} className="mr-1 text-gray-500" />
                                  Email
                                </label>
                                <input
                                  type="email"
                                  value={customerData.email || ''}
                                  onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                  placeholder="Email customer"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Gender */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                  <SafeIcon icon={FiUsers} className="mr-1 text-gray-500" />
                                  Jenis Kelamin
                                </label>
                                <select
                                  value={customerData.gender || ''}
                                  onChange={(e) => setCustomerData({ ...customerData, gender: e.target.value })}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                >
                                  <option value="">Pilih Jenis Kelamin</option>
                                  <option value="Laki-laki">Laki-laki</option>
                                  <option value="Perempuan">Perempuan</option>
                                </select>
                              </div>

                              {/* Birth Date */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                  <SafeIcon icon={FiCalendar} className="mr-1 text-gray-500" />
                                  Tanggal Lahir
                                </label>
                                <input
                                  type="date"
                                  value={customerData.birth_date || ''}
                                  onChange={(e) => setCustomerData({ ...customerData, birth_date: e.target.value })}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Lead Status */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                  <SafeIcon icon={FiTarget} className="mr-1 text-gray-500" />
                                  Status Lead
                                </label>
                                <select
                                  value={customerData.lead_status || 'cold'}
                                  onChange={(e) => setCustomerData({ ...customerData, lead_status: e.target.value })}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                >
                                  <option value="cold">Dingin</option>
                                  <option value="warm">Hangat</option>
                                  <option value="hot">Panas</option>
                                  <option value="customer">Pelanggan</option>
                                </select>
                              </div>

                              {/* Follow Up Date */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                  <SafeIcon icon={FiClock} className="mr-1 text-gray-500" />
                                  Tanggal Follow Up
                                </label>
                                <input
                                  type="datetime-local"
                                  value={customerData.follow_up_date || ''}
                                  onChange={(e) => setCustomerData({ ...customerData, follow_up_date: e.target.value })}
                                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                />
                              </div>
                            </div>

                            {/* Location */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <SafeIcon icon={FiMapPin} className="mr-1 text-gray-500" />
                                Lokasi
                              </label>
                              <input
                                type="text"
                                value={customerData.location || ''}
                                onChange={(e) => setCustomerData({ ...customerData, location: e.target.value })}
                                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                placeholder="Lokasi customer"
                              />
                            </div>

                            {/* Complaint */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <SafeIcon icon={FiMessageCircle} className="mr-1 text-gray-500" />
                                Keluhan
                              </label>
                              <textarea
                                value={customerData.complaint || ''}
                                onChange={(e) => setCustomerData({ ...customerData, complaint: e.target.value })}
                                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                placeholder="Keluhan customer"
                                rows="2"
                              ></textarea>
                            </div>

                            {/* Internal Notes */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center">
                                <SafeIcon icon={FiClipboard} className="mr-1 text-gray-500" />
                                Catatan Internal
                              </label>
                              <textarea
                                value={customerData.internal_notes || ''}
                                onChange={(e) => setCustomerData({ ...customerData, internal_notes: e.target.value })}
                                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                placeholder="Catatan internal (tidak terlihat oleh customer)"
                                rows="2"
                              ></textarea>
                            </div>

                            {/* Custom Fields */}
                            <div>
                              <div className="flex justify-between items-center mb-1.5">
                                <label className="block text-xs font-medium text-gray-700 flex items-center">
                                  <SafeIcon icon={FiFileText} className="mr-1 text-gray-500" />
                                  Field Tambahan
                                </label>
                              </div>

                              {/* Existing Custom Fields */}
                              <div className="space-y-2 mb-2">
                                {customFields.map((field, index) => (
                                  <div key={index} className="flex items-center space-x-1.5">
                                    <input
                                      type="text"
                                      value={field.field_name}
                                      onChange={(e) => updateCustomField(index, 'field_name', e.target.value)}
                                      className="w-1/3 px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                      placeholder="Nama field"
                                    />
                                    <input
                                      type="text"
                                      value={field.field_value}
                                      onChange={(e) => updateCustomField(index, 'field_value', e.target.value)}
                                      className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                      placeholder="Nilai field"
                                    />
                                    <button
                                      onClick={() => deleteCustomField(index)}
                                      className="text-red-500 hover:text-red-700 p-1"
                                    >
                                      <SafeIcon icon={FiTrash2} className="text-xs" />
                                    </button>
                                  </div>
                                ))}
                              </div>

                              {/* Add New Custom Field */}
                              <div className="flex items-center space-x-1.5">
                                <input
                                  type="text"
                                  value={newField.field_name}
                                  onChange={(e) => setNewField({ ...newField, field_name: e.target.value })}
                                  className="w-1/3 px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                  placeholder="Nama field baru"
                                />
                                <input
                                  type="text"
                                  value={newField.field_value}
                                  onChange={(e) => setNewField({ ...newField, field_value: e.target.value })}
                                  className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                  placeholder="Nilai field baru"
                                />
                                <button
                                  onClick={addCustomField}
                                  className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded-lg transition-colors"
                                  disabled={!newField.field_name.trim()}
                                >
                                  <SafeIcon icon={FiPlus} className="text-xs" />
                                </button>
                              </div>
                            </div>

                            {/* Enhanced Save Button */}
                            <div className="flex justify-end">
                              <button
                                onClick={saveCustomerData}
                                disabled={isSaving}
                                className={`bg-green-600 hover:bg-green-700 text-white py-1.5 px-3 rounded-lg transition-colors flex items-center space-x-1.5 text-xs ${
                                  isSaving ? 'opacity-70 cursor-not-allowed' : ''
                                }`}
                              >
                                {isSaving ? (
                                  <>
                                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                                    <span>Menyimpan...</span>
                                  </>
                                ) : (
                                  <>
                                    <SafeIcon icon={FiSave} className="text-xs" />
                                    <span>Simpan Data</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation Controls */}
                  <div className="flex items-center justify-center bg-gray-100 border-b border-gray-200 px-3 py-1.5 text-xs">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={scrollToTop}
                        className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                        title="Ke atas"
                      >
                        <SafeIcon icon={FiArrowUp} className="text-sm" />
                      </button>
                      <button
                        onClick={scrollToBottom}
                        className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                        title="Ke bawah"
                      >
                        <SafeIcon icon={FiArrowDown} className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Enhanced Messages Area */}
                  <div
                    ref={chatAreaRef}
                    className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50"
                    style={{
                      overscrollBehavior: 'contain',
                      minHeight: mobileView ? '40vh' : 'auto',
                      maxHeight: mobileView ? 'calc(100vh - 260px)' : 'calc(100vh - 260px)'
                    }}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                      </div>
                    ) : filteredConversations.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <SafeIcon icon={FiAlertCircle} className="text-3xl text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">Tidak ada percakapan ditemukan</p>
                        </div>
                      </div>
                    ) : (
                      <AnimatePresence>
                        {filteredConversations.map((message, index) => (
                          <motion.div
                            key={`${message.id}-${index}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: Math.min(index * 0.01, 0.3) }}
                            className={`flex ${message.jenis_pesan === 'keluar' ? 'justify-end' : 'justify-start'}`}
                            ref={index === 0 ? topMessageRef : null}
                          >
                            <div className={`max-w-xs lg:max-w-md px-3 py-2 rounded-xl relative message-menu-container ${
                              message.jenis_pesan === 'keluar'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-800 border border-gray-200'
                            }`}>
                              {/* Message content */}
                              <div className="break-words text-sm">
                                {searchTerm && message.isi_pesan.toLowerCase().includes(searchTerm.toLowerCase()) ? (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: message.isi_pesan.replace(
                                        new RegExp(`(${searchTerm})`, 'gi'),
                                        '<mark class="bg-yellow-200 rounded px-1">$1</mark>'
                                      )
                                    }}
                                  />
                                ) : (
                                  message.isi_pesan
                                )}
                              </div>

                              {/* Enhanced message metadata */}
                              <div className={`text-xs mt-1.5 ${
                                message.jenis_pesan === 'keluar' ? 'text-blue-100' : 'text-gray-500'
                              }`}>
                                <div className="flex items-center justify-between">
                                  <span>{formatTime(message.waktu_pesan)}</span>
                                  {!selectedConversation && (
                                    <span className="text-xs opacity-75 ml-2">
                                      {message.id_whatsapp}
                                      {customerNames[message.id_whatsapp] && (
                                        <span className="ml-1">({customerNames[message.id_whatsapp]})</span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Enhanced Admin Menu */}
                              {isAdmin && (
                                <div className="absolute top-1 right-1">
                                  <button
                                    onClick={() => toggleMessageMenu(message.id)}
                                    className={`p-1 rounded-full ${
                                      message.jenis_pesan === 'keluar'
                                        ? 'text-blue-100 hover:bg-blue-500'
                                        : 'text-gray-400 hover:bg-gray-100'
                                    }`}
                                  >
                                    <SafeIcon icon={FiMoreVertical} className="text-xs" />
                                  </button>
                                  {activeMessageMenu === message.id && (
                                    <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg py-1 z-10 w-36">
                                      <button
                                        onClick={() => {
                                          setMessageToDelete(message);
                                          setShowDeleteModal(true);
                                          setActiveMessageMenu(null);
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-gray-100 flex items-center"
                                      >
                                        <SafeIcon icon={FiTrash2} className="mr-1.5" />
                                        Hapus Pesan
                                      </button>
                                      <button
                                        onClick={() => {
                                          navigator.clipboard.writeText(message.isi_pesan);
                                          setActiveMessageMenu(null);
                                          addNotification({ type: 'success', message: 'Pesan disalin ke clipboard' });
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-xs text-blue-600 hover:bg-gray-100 flex items-center"
                                      >
                                        <SafeIcon icon={FiClipboard} className="mr-1.5" />
                                        Salin Pesan
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                        <div ref={messagesEndRef} />
                      </AnimatePresence>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Delete Message Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="bg-red-600 text-white p-4 rounded-t-xl flex items-center">
                <SafeIcon icon={FiAlertTriangle} className="text-xl mr-2" />
                <h3 className="font-semibold">Konfirmasi Hapus Pesan</h3>
              </div>
              <div className="p-5">
                <p className="text-gray-700 mb-3">
                  Apakah Anda yakin ingin menghapus pesan ini? Tindakan ini tidak dapat dibatalkan.
                </p>
                {messageToDelete && (
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4 text-sm">
                    <div className="font-medium text-gray-600 mb-1">Isi pesan:</div>
                    <div className="text-gray-800">{messageToDelete.isi_pesan}</div>
                    <div className="text-xs text-gray-500 mt-1">{formatTime(messageToDelete.waktu_pesan)}</div>
                  </div>
                )}
                {deleteError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">{deleteError}</div>
                )}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setMessageToDelete(null);
                      setDeleteError(null);
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleDeleteMessage}
                    disabled={isDeleting}
                    className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2 ${
                      isDeleting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isDeleting ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Menghapus...</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiTrash2} />
                        <span>Hapus</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Delete All Modal */}
      <AnimatePresence>
        {showDeleteAllModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="bg-red-600 text-white p-4 rounded-t-xl flex items-center">
                <SafeIcon icon={FiAlertTriangle} className="text-xl mr-2" />
                <h3 className="font-semibold">Konfirmasi Hapus Semua Percakapan</h3>
              </div>
              <div className="p-5">
                <p className="text-gray-700 mb-3">
                  <strong>PERINGATAN:</strong> Anda akan menghapus <strong>SEMUA</strong> pesan dari nomor{' '}
                  <strong>{selectedConversation}</strong>.
                </p>
                <p className="text-gray-700 mb-5 font-medium">
                  Tindakan ini tidak dapat dibatalkan dan semua data percakapan akan hilang permanen!
                </p>
                <div className="bg-red-50 p-3 rounded-lg border border-red-200 mb-4">
                  <div className="flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-600 mr-2" />
                    <span className="text-red-700 font-medium">
                      {filteredConversations.length} pesan akan dihapus
                    </span>
                  </div>
                </div>
                {deleteError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">{deleteError}</div>
                )}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteAllModal(false);
                      setDeleteError(null);
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleDeleteAllMessages}
                    disabled={isDeleting}
                    className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2 ${
                      isDeleting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isDeleting ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Menghapus...</span>
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiTrash2} />
                        <span>Hapus Semua</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatDashboard;